<template>
  <card-container
    style="padding: 20px; padding-bottom: 0; overflow: visible"
    class="tooth-card is-v2-form"
  >
    <div class="loading-container" v-if="isLoading">
      <div class="loading-inner-container">
        <div class="spinner" />
      </div>
    </div>

    <!-- 치아 번호 선택 섹션 (전체 너비) -->
    <div class="teeth-number-section">
      <row-with-label
        :label-title="$t('workorder_form_tooth_number')"
        :is-required="true"
        :has-error="false"
        :is-column="false"
        :marginTopLabel="true"
      >
        <div class="teeth-number-selector">
          <input
            class="ant-like-input"
            type="text"
            style="border-radius: 6px; width: 100%; margin-bottom: 12px"
            :value="selectedTeethDisplay"
            :placeholder="$t('workorder_form_select_tooth_placeholder')"
            readonly
          />
          <teeth-area
            :isSafari="isSafari"
            :selectedTeethNumbers="localToothNumbers"
            @selectTooth="handleToothSelection"
            :getsFixedHeight="true"
          />
        </div>
      </row-with-label>

      <!-- 선택된 치아 번호 표시 -->
      <div v-if="localToothNumbers.length > 0" class="selected-teeth-display">
        {{ formattedSelectedTeeth }}
      </div>
    </div>

    <!-- 2열 레이아웃: 왼쪽(입력 폼), 오른쪽(인증서 이미지) -->
    <div class="teeth-item-wrap">
      <div class="teeth-contents-area">
        <!-- 시술방식 선택 -->
        <row-with-label
          :label-title="$t('workorder_form_treatment_type')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                value="CROWN"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_crown") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="IMPLANT_CROWN"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_implant_crown") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="BRIDGE"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_bridge") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="INLAY"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_inlay") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="LAMINATE"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_laminate") }}</span>
            </label>
          </div>
        </row-with-label>

        <!-- 제조사 컨테이너 -->
        <row-with-label
          :label-title="$t('workorder_form_manufacturer')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div
            style="
              position: relative;
              display: flex;
              flex-direction: column;
              gap: 24px;
              z-index: 4;
            "
          >
            <div class="is-certified-wrap">
              <span class="is-certified-label">
                <div class="left-area">
                  <div class="companies-wrap">
                    <button
                      class="company-chip-wrap"
                      v-for="certifiedBrand in sortedCertifiedCompanies"
                      :key="certifiedBrand.name"
                      :class="
                        localToothInfo.isCertified &&
                        certifiedBrand.name === localToothInfo.group
                          ? 'selected'
                          : ''
                      "
                      :name="`certification${arrayIndex}`"
                      :id="`${certifiedBrand.name}${arrayIndex}`"
                      @click="handleGroupClick(certifiedBrand.name, true)"
                    >
                      <div class="certified-brand">
                        <img :src="certifiedBrand.iconUrl" />
                      </div>
                      <span
                        class="product-company-label"
                        :for="`${certifiedBrand.name}${arrayIndex}`"
                      >
                        {{ getCompanyDisplayName(certifiedBrand.name) }}
                        <i class="fa-solid fa-badge-check" />
                      </span>
                    </button>
                  </div>
                </div>
                <div class="right-area">
                  <button
                    class="change-order-btn"
                    @click="openManufacturerSettingModal"
                  >
                    <i class="fa-regular fa-arrow-right-arrow-left" />
                    {{ $t("workorder_form_change_order") }}
                  </button>
                </div>
              </span>
            </div>
          </div>
        </row-with-label>

        <!-- 상세 선택 -->
        <row-with-label
          :label-title="$t('workorder_form_detail_selection')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                value="ZIRCONIA"
                v-model="localToothInfo.detailSelection"
                @change="handleDetailSelectionChange"
              />
              <span>{{ $t("workorder_form_zirconia") }}</span>
            </label>
          </div>
        </row-with-label>

        <!-- 입력 방식 선택 (덴탈맥스 선택 시에만 표시) -->
        <row-with-label
          v-if="localToothInfo.group === '덴탈맥스'"
          :label-title="$t('workorder_form_input_mode')"
          :is-required="false"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="input-mode-selector">
            <a-radio-group
              v-model:value="inputMode"
              @change="handleInputModeChange"
            >
              <a-radio value="barcode">{{
                $t("workorder_form_barcode_scan")
              }}</a-radio>
              <a-radio value="manual">{{
                $t("workorder_form_manual_input")
              }}</a-radio>
            </a-radio-group>
          </div>
        </row-with-label>

        <!-- 바코드 스캔 모드 (덴탈맥스 + barcode 모드) -->
        <row-with-label
          v-if="localToothInfo.group === '덴탈맥스' && inputMode === 'barcode'"
          :label-title="$t('workorder_form_barcode_scan')"
          :is-required="false"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="barcode-scan-container">
            <a-input
              ref="barcodeScanInput"
              v-model:value="barcodeScanValue"
              :placeholder="$t('workorder_form_barcode_scan_placeholder')"
              @change="handleBarcodeScan"
              @keyup.enter="handleBarcodeScan"
            >
              <template #prefix>
                <i class="fa-solid fa-barcode" style="color: #8d939a" />
              </template>
              <template #suffix>
                <a-button
                  v-if="barcodeScanValue"
                  type="text"
                  size="small"
                  @click="clearBarcodeData"
                >
                  <i class="fa-solid fa-xmark" />
                </a-button>
              </template>
            </a-input>
            <div v-if="barcodeParseError" class="barcode-error">
              {{ barcodeParseError }}
            </div>
          </div>
        </row-with-label>

        <!-- 바코드 스캔 파싱 결과 표시 (barcode 모드) -->
        <div
          v-if="
            localToothInfo.group === '덴탈맥스' &&
            inputMode === 'barcode' &&
            hasParsedData
          "
          class="parsed-data-section"
        >
          <div class="parsed-data-grid">
            <div class="parsed-data-item">
              <span class="parsed-label">{{
                $t("workorder_form_gtin_code_01")
              }}</span>
              <span class="parsed-value">{{ gtinCode || "-" }}</span>
            </div>
            <div class="parsed-data-item">
              <span class="parsed-label">{{
                $t("workorder_form_manufacture_date_11")
              }}</span>
              <span class="parsed-value">{{
                formatManufacturingDate(manufacturingDate) || "-"
              }}</span>
            </div>
            <div class="parsed-data-item">
              <span class="parsed-label">{{
                $t("workorder_form_lot_10")
              }}</span>
              <span class="parsed-value">{{ lotNumber || "-" }}</span>
            </div>
          </div>
        </div>

        <!-- 직접 입력 모드 (덴탈맥스 + manual 모드) -->
        <template
          v-if="localToothInfo.group === '덴탈맥스' && inputMode === 'manual'"
        >
          <!-- GTIN 코드 입력 (필수) -->
          <row-with-label
            :label-title="$t('workorder_form_gtin_code')"
            :is-required="true"
            :has-error="!!manualGtinError"
            :is-column="!windowInnerWidthGreaterThan1000"
          >
            <template #labelSuffix>
              <a-tooltip :title="$t('workorder_tooltip_gtin')">
                <i
                  class="fa-solid fa-circle-question"
                  style="color: #8d939a; margin-left: 4px; cursor: help"
                />
              </a-tooltip>
            </template>
            <div class="manual-input-wrapper">
              <a-auto-complete
                v-model:value="gtinCode"
                :options="gtinAutoCompleteOptions"
                @search="handleGtinSearch"
                @select="handleGtinSelect"
                @blur="handleGtinBlur"
              >
                <a-input
                  :maxlength="14"
                  :placeholder="$t('workorder_form_gtin_placeholder')"
                >
                  <template #prefix>
                    <span class="gs1-prefix">(01)</span>
                  </template>
                </a-input>
              </a-auto-complete>
              <div v-if="manualGtinError" class="manual-input-error">
                {{ manualGtinError }}
              </div>
            </div>
          </row-with-label>

          <!-- 생산일자 입력 (선택) -->
          <row-with-label
            :label-title="$t('workorder_form_manufacture_date')"
            :is-required="false"
            :has-error="!!manufactureDateError"
            :is-column="!windowInnerWidthGreaterThan1000"
          >
            <template #labelSuffix>
              <a-tooltip :title="$t('workorder_tooltip_manufacture_date')">
                <i
                  class="fa-solid fa-circle-question"
                  style="color: #8d939a; margin-left: 4px; cursor: help"
                />
              </a-tooltip>
            </template>
            <div class="manual-input-wrapper">
              <a-input
                v-model:value="manufacturingDate"
                :placeholder="$t('workorder_form_manufacture_date_placeholder')"
                :maxlength="6"
                @change="handleManualManufacturingDateChange"
              >
                <template #prefix>
                  <span class="gs1-prefix">(11)</span>
                </template>
              </a-input>
              <div v-if="manufactureDateError" class="manual-input-error">
                {{ manufactureDateError }}
              </div>
            </div>
          </row-with-label>

          <!-- LOT 번호 입력 (선택) -->
          <row-with-label
            :label-title="$t('workorder_form_lot_number')"
            :is-required="false"
            :has-error="false"
            :is-column="!windowInnerWidthGreaterThan1000"
          >
            <template #labelSuffix>
              <a-tooltip :title="$t('workorder_tooltip_lot')">
                <i
                  class="fa-solid fa-circle-question"
                  style="color: #8d939a; margin-left: 4px; cursor: help"
                />
              </a-tooltip>
            </template>
            <a-input
              v-model:value="lotNumber"
              :placeholder="$t('workorder_form_lot_placeholder')"
              :maxlength="20"
              @change="handleManualLotNumberChange"
            >
              <template #prefix>
                <span class="gs1-prefix">(10)</span>
              </template>
            </a-input>
          </row-with-label>
        </template>

        <!-- 제품 선택 -->
        <row-with-label
          :label-title="$t('workorder_form_product_selection')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <!-- 덴탈맥스 선택 시 셀렉트박스 표시 -->
          <div
            v-if="localToothInfo.group === '덴탈맥스'"
            class="product-select-wrapper"
          >
            <a-select
              v-model:value="localToothInfo.itemName"
              :placeholder="$t('workorder_form_product_name_placeholder')"
              style="width: 100%"
              :disabled="!!gtinSearchError || !!gtinCode"
              @change="handleDentalMaxProductChange"
            >
              <a-select-option
                v-for="product in dentalMaxProducts"
                :key="product"
                :value="product"
              >
                {{ product }}
              </a-select-option>
            </a-select>
            <!-- GTIN 검색 에러 메시지 -->
            <div v-if="gtinSearchError" class="gtin-error-message">
              {{ gtinSearchError }}
            </div>
          </div>

          <!-- 다른 제조사는 기존 input 유지 -->
          <a-input
            v-else
            :value="localToothInfo.itemName"
            :placeholder="$t('workorder_form_product_name_placeholder')"
            readonly
            @click="handleProductClick"
          />
        </row-with-label>
        <!-- STM 표면처리 컨테이너 (덴탈맥스 선택 시에만 표시) -->
        <div
          class="surface-container"
          v-if="localToothInfo.group === '덴탈맥스'"
        >
          <div class="surface-title-area">
            <div class="surface-text-button">
              <div class="tooltip-label-wrap">
                <div
                  class="question-mark"
                  @click="modalHandler('stmModal', true)"
                >
                  <i class="fa-solid fa-question" />
                </div>
                <div class="label-wrap">
                  <div class="label-text" style="margin-bottom: 0px">
                    {{ $t("workorder_form_stm_surface_treatment") }}
                  </div>
                </div>
              </div>
              <div class="switch-text-wrap">
                <a-switch
                  v-model:checked="localToothInfo.switches.stm"
                  @change="toggleStmSwitch"
                />
              </div>
            </div>
            <p
              class="true-color"
              :style="
                localToothInfo.switches.stm
                  ? 'color: rgb(39, 138, 236);'
                  : 'color: #ACB2B9;'
              "
            >
              {{
                localToothInfo.switches.stm
                  ? $t("workorder_form_stm_completed")
                  : $t("workorder_form_stm_not_processed")
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- 인증서 이미지 영역 -->
      <div class="certificate-img-wrapper">
        <!-- STM 적용 시 -->
        <div v-if="showStmCertificateImage" class="certificate-img-area">
          <div class="certificate-img full">
            <img :src="companyLogoUrl" />
            <img class="stm-overlay-img" :src="stmCertificateImageUrl" />
          </div>
        </div>
        <!-- 기본 상태 -->
        <div v-else class="certificate-img-area">
          <div
            class="certificate-img full"
            :ref="`certificate-img-${arrayIndex}`"
            :class="isFlipped ? 'flip' : ''"
          >
            <img :src="companyLogoUrl" />
            <div class="card-back"><img :src="companyLogoUrl" /></div>
          </div>
        </div>
      </div>
    </div>
  </card-container>

  <!-- STM 모달 -->
  <stm-modal :showModal="modals.stmModal.show" @closeModal="modalHandler" />
</template>

<script>
import CardContainer from "./CardContainer.vue";
import RowWithLabel from "@/views/components/RowWithLabel";
import TeethArea from "./TeethArea.vue";
import StmModal from "./StmModal.vue";
import apiInstance from "@/axios";

export default {
  name: "ZirconiaToothInfoForm",
  components: {
    CardContainer,
    RowWithLabel,
    TeethArea,
    StmModal,
  },
  props: {
    toothInfo: {
      type: Object,
      required: true,
    },
    arrayIndex: {
      type: Number,
      required: true,
    },
    windowInnerWidthGreaterThan1000: {
      type: Boolean,
      default: false,
    },
    modalShowHistory: {
      type: Object,
      default: () => ({}),
    },
    certifiedCompanies: {
      type: Array,
      default: () => [],
    },
    notCertifiedCompanies: {
      type: Array,
      default: () => [],
    },
    selectedTeethNumbers: {
      type: Array,
      default: () => [],
    },
    prevTeethNos: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "update:tooth-info",
    "update:modal-show-history",
    "update-manufacturer-list",
  ],
  data() {
    const initialToothNumbers = this.toothInfo?.toothNumbers
      ? [...this.toothInfo.toothNumbers]
      : [];

    return {
      isLoading: false,
      isFlipped: false,
      isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
      localToothNumbers: initialToothNumbers,
      localTreatmentType: this.toothInfo?.treatmentType || null,
      localToothInfo: {
        ...this.toothInfo,
        toothNumbers: initialToothNumbers,
        treatmentType: this.toothInfo?.treatmentType || null,
        manufacturerId: "",
        switches: {
          stm: this.toothInfo?.switches?.stm || false,
          ...this.toothInfo?.switches,
        },
        group: this.toothInfo?.group || "덴탈맥스",
        isCertified: this.toothInfo?.isCertified !== false,
        itemName: this.toothInfo?.itemName || "",
        detailSelection: this.toothInfo?.detailSelection || "ZIRCONIA",
      },
      modals: {
        stmModal: {
          show: false,
        },
        history: {
          stm: false,
        },
      },
      dentalMaxProducts: [],
      isDentalMaxProductsLoading: false,
      // GTIN 검색 관련 상태
      gtinCode: this.toothInfo?.productInfoWithCode?.gtinCode || "",
      gtinSearchResults: [],
      isGtinSearching: false,
      gtinSearchError: "",
      gtinDebounceTimer: null,
      showGtinDropdown: false,
      // 제조일자 및 LOT 상태
      manufacturingDate:
        this.toothInfo?.productInfoWithCode?.manufacturingDate || "",
      lotNumber: this.toothInfo?.productInfoWithCode?.lotNumber || "",
      // 입력 모드 ('barcode' | 'manual')
      inputMode: this.toothInfo?.inputMode || "barcode",
      // 직접 입력 모드 에러 상태
      manualGtinError: "",
      manufactureDateError: "",
      // 바코드 스캔 관련 상태
      barcodeScanValue: "",
      barcodeParseError: "",
      // 카드 애니메이션 중복 방지 플래그
      flipAnimationPending: false,
    };
  },
  computed: {
    sortedCertifiedCompanies() {
      return this.certifiedCompanies.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
    },
    companyLogoUrl() {
      // zh locale인 경우 중국어 이미지 사용
      if (this.$i18n.locale === "zh") {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese.png";
      } else {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/Zirconia.png";
      }
    },
    showStmCertificateImage() {
      return (
        this.localToothInfo.group === "덴탈맥스" &&
        this.localToothInfo.switches?.stm
      );
    },
    stmCertificateImageUrl() {
      // zh locale인 경우 STM 중국어 이미지 사용
      if (this.$i18n.locale === "zh") {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/stm_chinese.png";
      } else {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/dentalmax_stm.png";
      }
    },
    selectedTeethDisplay() {
      if (this.localToothNumbers.length === 0) return "";
      return this.localToothNumbers.sort((a, b) => a - b).join(", ");
    },
    formattedSelectedTeeth() {
      if (this.localToothNumbers.length === 0) return "";
      const sorted = [...this.localToothNumbers].sort((a, b) => a - b);
      return this.$t("workorder_form_tooth_format", {
        teeth: sorted.join(", "),
      });
    },
    // GTIN 자동완성 옵션
    gtinAutoCompleteOptions() {
      return this.gtinSearchResults.map((product) => ({
        value: product.gtinCode,
        label: `${product.productLine || ""}`.trim(),
        product: product,
      }));
    },
    // 파싱된 데이터가 있는지 확인
    hasParsedData() {
      return !!(this.gtinCode || this.manufacturingDate || this.lotNumber);
    },
  },
  watch: {
    toothInfo: {
      handler(newVal) {
        if (newVal) {
          this.localToothInfo = {
            ...newVal,
            switches: {
              ...this.localToothInfo.switches,
              ...newVal.switches,
            },
          };

          if (
            newVal.toothNumbers !== undefined &&
            Array.isArray(newVal.toothNumbers)
          ) {
            this.localToothNumbers = [...newVal.toothNumbers];
          }

          if (newVal.treatmentType) {
            this.localTreatmentType = newVal.treatmentType;
          }

          // GTIN 코드 복원
          if (newVal.productInfoWithCode?.gtinCode) {
            this.gtinCode = newVal.productInfoWithCode.gtinCode;
          }
          // 제조일자 복원
          if (newVal.productInfoWithCode?.manufacturingDate) {
            this.manufacturingDate =
              newVal.productInfoWithCode.manufacturingDate;
          }
          // LOT 번호 복원
          if (newVal.productInfoWithCode?.lotNumber) {
            this.lotNumber = newVal.productInfoWithCode.lotNumber;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    certifiedCompanies: {
      handler(newVal) {
        // 인증 제조사가 변경되었을 때도 자동 선택 시도
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.autoSelectSingleManufacturer();
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // 컴포넌트 마운트 시 제조사 자동 선택
    this.autoSelectSingleManufacturer();

    // 덴탈맥스 제품 목록 API 호출
    this.loadDentalMaxProducts();

    // 덴탈맥스 선택 시 바코드 입력란에 자동 포커스
    this.$nextTick(() => {
      this.focusBarcodeInput();
    });
  },
  methods: {
    // 덴탈맥스 제품 목록 API 호출
    async loadDentalMaxProducts() {
      this.isDentalMaxProductsLoading = true;
      try {
        const response = await this.$apiInstance.get("/items/brand/LUXEN");
        const items = response.data?.docs || response.data;
        if (items && Array.isArray(items)) {
          const productNames = items.map((item) => item.itemName);

          // 원하는 순서대로 정렬
          const sortOrder = [
            "LUXEN 5G ML",
            "LUXEN 5G Smile",
            "LUXEN 1200+600 Multi",
            "LUXEN 1100 Multi",
            "LUXEN 1100 Enamel",
            "LUXEN 1200 Zr",
          ];

          this.dentalMaxProducts = productNames.sort((a, b) => {
            const indexA = sortOrder.indexOf(a);
            const indexB = sortOrder.indexOf(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });

          console.log(
            "[덴탈맥스 제품 목록 로드 완료]:",
            this.dentalMaxProducts
          );
        } else {
          console.warn(
            "[덴탈맥스 제품 목록] 예상하지 못한 응답 형식:",
            response.data
          );
          this.setDefaultDentalMaxProducts();
        }
      } catch (error) {
        console.error("[덴탈맥스 제품 목록 로드 실패]:", error);
        this.setDefaultDentalMaxProducts();
      } finally {
        this.isDentalMaxProductsLoading = false;
      }
    },

    // 기본 덴탈맥스 제품 목록 설정 (API 실패 시)
    setDefaultDentalMaxProducts() {
      this.dentalMaxProducts = [
        "LUXEN 5G ML",
        "LUXEN 5G Smile",
        "LUXEN 1200+600 Multi",
        "LUXEN 1100 Multi",
        "LUXEN 1100 Enamel",
        "LUXEN 1200 Zr",
      ];
    },

    // 제조사 이름 표시 (중국어일 때 DENTALMAX로 표시)
    getCompanyDisplayName(companyName) {
      if (
        this.$i18n.locale === "zh" &&
        (companyName === "덴탈맥스" || companyName === "DENTALMAX")
      ) {
        return "DENTALMAX";
      }
      return companyName;
    },

    handleToothSelection(toothNumber) {
      if (!this.localToothNumbers) {
        this.localToothNumbers = [];
      }

      const index = this.localToothNumbers.indexOf(toothNumber);
      if (index > -1) {
        // 선택 해제
        this.localToothNumbers.splice(index, 1);
      } else {
        // 선택 - 중복 선택 허용
        this.localToothNumbers.push(toothNumber);
      }

      this.localToothInfo.toothNumbers = [...this.localToothNumbers];
      this.emitToothInfo();
    },

    handleTreatmentTypeChange() {
      this.localToothInfo.treatmentType = this.localTreatmentType;
      this.emitToothInfo();
    },

    handleDetailSelectionChange() {
      this.emitToothInfo();
    },

    handleGroupClick(groupName, isCertified) {
      const defaultItemName = this.getDefaultItemName(groupName);
      const selectedCompany = this.certifiedCompanies.find(
        (company) => company.name === groupName
      );
      const manufacturerId = selectedCompany?._id || selectedCompany?.id || "";

      this.localToothInfo = {
        ...this.localToothInfo,
        group: groupName,
        isCertified: isCertified,
        customGroup: groupName === "etc" ? "" : groupName,
        itemName: defaultItemName,
        manufacturerId: manufacturerId,
        productInfoWithoutCode: {
          ...this.localToothInfo.productInfoWithoutCode,
          brand: defaultItemName,
        },
      };
      this.emitToothInfo();
      this.performFlipEffect();
    },

    getDefaultItemName(groupName) {
      const selectedCompany = this.certifiedCompanies.find(
        (company) => company.name === groupName
      );
      return selectedCompany?.customFields?.itemName || "";
    },

    handleDentalMaxProductChange() {
      this.localToothInfo.productInfoWithoutCode.brand =
        this.localToothInfo.itemName;
      this.performFlipEffect();
      this.emitToothInfo();
    },

    handleProductClick() {
      if (this.localToothInfo.itemName) {
        this.performFlipEffect();
      }
    },

    toggleStmSwitch(checked) {
      if (!this.localToothInfo.switches) {
        this.localToothInfo.switches = {};
      }

      this.localToothInfo.switches.stm = checked;

      if (checked && !this.modals.history.stm) {
        this.modalHandler("stmModal", true);
        this.modals.history.stm = true;
      }

      this.emitToothInfo();
    },

    modalHandler(modalType, action) {
      if (!this.modals[modalType]) return;
      this.modals[modalType].show = action;
    },

    openManufacturerSettingModal() {
      // 순서 변경 모달 (필요시 구현)
    },

    performFlipEffect() {
      // 카드 1회전 애니메이션 (flip -> flip back)
      if (this.isFlipped) return; // 이미 애니메이션 중이면 무시

      setTimeout(() => {
        this.isFlipped = true; // 0 -> 180도 회전
        setTimeout(() => {
          this.isFlipped = false; // 180 -> 0도 회전 (원래 위치로)
        }, 600); // 0.6초 후 복귀
      }, 10);
    },

    // 카드 애니메이션 1회만 실행 (중복 방지)
    performFlipEffectOnce() {
      if (this.flipAnimationPending) return;
      this.flipAnimationPending = true;
      setTimeout(() => {
        this.isFlipped = !this.isFlipped;
        // 애니메이션 완료 후 플래그 리셋 (1초 후)
        setTimeout(() => {
          this.flipAnimationPending = false;
        }, 1000);
      }, 10);
    },

    // 바코드 입력란에 포커스
    focusBarcodeInput() {
      // 덴탈맥스 선택 시에만 포커스
      if (
        this.localToothInfo.group === "덴탈맥스" &&
        this.$refs.barcodeScanInput
      ) {
        // Ant Design Input 컴포넌트의 focus 메서드 호출
        if (this.$refs.barcodeScanInput.focus) {
          this.$refs.barcodeScanInput.focus();
        } else if (this.$refs.barcodeScanInput.$el) {
          // fallback: 네이티브 input 요소에 직접 포커스
          const inputEl =
            this.$refs.barcodeScanInput.$el.querySelector("input");
          if (inputEl) inputEl.focus();
        }
      }
    },

    emitToothInfo() {
      const toothInfoToEmit = JSON.parse(JSON.stringify(this.localToothInfo));
      // GTIN 에러 상태도 함께 전달
      toothInfoToEmit.gtinSearchError = this.gtinSearchError || "";
      // 입력 모드 상태 전달 (카드 확인/복원 시 보존용)
      toothInfoToEmit.inputMode = this.inputMode;
      this.$emit("update:tooth-info", toothInfoToEmit);
    },

    // 인증 제조사가 1개만 있을 때 자동 선택
    autoSelectSingleManufacturer() {
      // 이미 제조사가 선택되어 있으면 스킵
      if (this.localToothInfo.group) {
        return;
      }

      // 인증 제조사가 정확히 1개만 있는지 확인
      if (
        this.sortedCertifiedCompanies &&
        this.sortedCertifiedCompanies.length === 1
      ) {
        const singleCompany = this.sortedCertifiedCompanies[0];
        console.log("[자동 선택] 인증 제조사 1개 감지:", singleCompany.name);

        // 자동으로 선택
        this.handleGroupClick(singleCompany.name, true);
      }
    },

    // GTIN 검색 핸들러 (a-auto-complete의 @search 이벤트)
    handleGtinSearch(searchText) {
      // 숫자만 입력 가능하도록 필터링
      const value = searchText.replace(/[^0-9]/g, "");

      // 숫자가 아닌 문자가 입력되면 필터링된 값으로 교체
      if (value !== searchText) {
        this.gtinCode = value;
      }

      // 이전 타이머 취소
      if (this.gtinDebounceTimer) {
        clearTimeout(this.gtinDebounceTimer);
      }

      // 입력값 초기화 시 상태도 초기화
      if (!value || value.length < 8) {
        this.gtinSearchResults = [];
        this.gtinSearchError = "";
        this.showGtinDropdown = false;
        return;
      }

      // 300ms debounce 적용
      this.gtinDebounceTimer = setTimeout(() => {
        this.performGtinSearch(value);
      }, 300);
    },

    // GTIN 검색 수행
    async performGtinSearch(gtinCode) {
      this.isGtinSearching = true;
      this.gtinSearchError = "";
      this.manualGtinError = "";
      this.gtinSearchResults = [];
      this.showGtinDropdown = false;

      try {
        // 14자리 입력 시 13자리로 정규화하여 검색
        const normalizedCode = this.normalizeGtinCode(gtinCode);
        // 실제 API 호출
        const results = await this.searchProductByGtin(normalizedCode);

        if (results.length === 0) {
          const errorMsg = this.$t("workorder_err_unregistered_barcode");
          this.gtinSearchError = errorMsg;
          if (this.inputMode === "manual") {
            this.manualGtinError = errorMsg;
          }
          this.localToothInfo.itemName = "";
          this.gtinSearchResults = [];
          this.showGtinDropdown = false;
        } else if (results.length === 1 && normalizedCode.length === 13) {
          // 13자리 정확 일치 시 자동 선택 (14자리 입력도 정규화 후 13자리면 자동 선택)
          this.gtinSearchResults = results;
          this.showGtinDropdown = false;
          this.applyGtinProductSelection(results[0]);
        } else {
          // 10~12자리 또는 여러 결과인 경우 드롭다운 표시
          this.gtinSearchResults = results;
          this.showGtinDropdown = true;
        }
      } catch (error) {
        console.error("[GTIN 검색 오류]", error);
        this.showGtinDropdown = false;
        let errorMsg = "";
        if (error.response?.status === 400) {
          errorMsg = this.$t("workorder_err_gtin_invalid_request");
        } else if (error.response?.status === 401) {
          errorMsg = this.$t("workorder_err_gtin_auth_required");
        } else {
          errorMsg = this.$t("workorder_err_gtin_search_error");
        }
        this.gtinSearchError = errorMsg;
        if (this.inputMode === "manual") {
          this.manualGtinError = errorMsg;
        }
      } finally {
        this.isGtinSearching = false;
      }
    },

    // GTIN 코드로 제품 검색 API 호출
    async searchProductByGtin(gtinCode) {
      // locale에 따라 국가별 API 엔드포인트 분기 (zh → cn, 그 외 → kr)
      const country = this.$store.getters.getLocale === "zh" ? "cn" : "kr";
      const response = await apiInstance.get(
        `/gtin-products/${country}/autocomplete`,
        {
          params: {
            q: gtinCode,
            limit: 20,
            status: "active",
          },
        }
      );

      // API 응답 데이터 구조: { success: true, data: [...], meta: {...} }
      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    },

    // GTIN 자동완성 드롭다운에서 제품 선택
    handleGtinSelect(selectedGtinCode) {
      const selectedProduct = this.gtinSearchResults.find(
        (product) => product.gtinCode === selectedGtinCode
      );

      if (selectedProduct) {
        this.gtinCode = selectedProduct.gtinCode;
        this.showGtinDropdown = false;
        this.applyGtinProductSelection(selectedProduct);
      }
    },

    // GTIN 입력란 blur 시 드롭다운 닫기
    handleGtinBlur() {
      // 약간의 딜레이를 줘서 select 이벤트가 먼저 처리되도록 함
      setTimeout(() => {
        this.showGtinDropdown = false;
      }, 200);
    },

    // 선택한 GTIN 제품을 로컬 데이터에 적용
    applyGtinProductSelection(product) {
      // API 응답: { _id, gtinCode, brandName, modelName, productLine, status }
      const productName = product.productLine || product.brandName;

      this.localToothInfo.itemName = productName;
      this.localToothInfo.productInfoWithoutCode.brand = productName;
      this.localToothInfo.productInfoWithCode = {
        id: product._id || product.gtinCode,
        productCode: product.gtinCode,
        itemName: productName,
        brandName: product.brandName,
        modelName: product.modelName,
        productLine: product.productLine,
        gtinCode: product.gtinCode,
        manufacturingDate: this.manufacturingDate,
        lotNumber: this.lotNumber,
      };

      this.performFlipEffect();
      this.emitToothInfo();
    },

    // 제조일자 변경 핸들러
    handleManufacturingDateChange() {
      // 숫자만 허용
      this.manufacturingDate = this.manufacturingDate.replace(/[^0-9]/g, "");

      // productInfoWithCode 업데이트
      if (!this.localToothInfo.productInfoWithCode) {
        this.localToothInfo.productInfoWithCode = {};
      }
      this.localToothInfo.productInfoWithCode.manufacturingDate =
        this.manufacturingDate;
      this.emitToothInfo();
    },

    // LOT 번호 변경 핸들러
    handleLotNumberChange() {
      // productInfoWithCode 업데이트
      if (!this.localToothInfo.productInfoWithCode) {
        this.localToothInfo.productInfoWithCode = {};
      }
      this.localToothInfo.productInfoWithCode.lotNumber = this.lotNumber;
      this.emitToothInfo();
    },

    // GTIN 코드 정규화 (14자리 -> 13자리)
    normalizeGtinCode(code) {
      if (!code) return "";
      // 14자리이고 0으로 시작하면 앞자리 0 제거
      if (code.length === 14 && code.startsWith("0")) {
        return code.slice(1);
      }
      return code;
    },

    // 바코드 스캔 핸들러 - GS1-128 파싱
    handleBarcodeScan() {
      const rawValue = this.barcodeScanValue.trim();
      this.barcodeParseError = "";

      if (!rawValue) {
        this.clearBarcodeData();
        return;
      }

      try {
        // 한글 키보드 입력을 영문으로 변환
        const convertedValue = this.convertKoreanToEnglish(rawValue);

        // 입력란도 영문으로 변환된 값으로 업데이트
        if (convertedValue !== rawValue) {
          this.barcodeScanValue = convertedValue;
        }

        const parsed = this.parseGS1Barcode(convertedValue);

        if (parsed.gtin) {
          this.gtinCode = parsed.gtin;
        }
        if (parsed.manufacturingDate) {
          this.manufacturingDate = parsed.manufacturingDate;
        }
        if (parsed.lotNumber) {
          this.lotNumber = parsed.lotNumber;
        }

        // productInfoWithCode 업데이트
        if (!this.localToothInfo.productInfoWithCode) {
          this.localToothInfo.productInfoWithCode = {};
        }
        this.localToothInfo.productInfoWithCode.gtinCode = this.gtinCode;
        this.localToothInfo.productInfoWithCode.manufacturingDate =
          this.manufacturingDate;
        this.localToothInfo.productInfoWithCode.lotNumber = this.lotNumber;

        // GTIN 코드로 제품 검색 수행 및 카드 애니메이션 1회 실행
        if (parsed.gtin) {
          this.performGtinSearch(this.normalizeGtinCode(parsed.gtin));
          this.performFlipEffectOnce();
        }

        this.emitToothInfo();
      } catch (error) {
        console.error("[바코드 파싱 오류]", error);
        this.barcodeParseError = this.$t("workorder_err_barcode_format");
      }
    },

    // 한글 키보드 입력을 영문으로 변환
    convertKoreanToEnglish(str) {
      const korToEng = {
        // 자음
        ㅂ: "q",
        ㅈ: "w",
        ㄷ: "e",
        ㄱ: "r",
        ㅅ: "t",
        ㅛ: "y",
        ㅕ: "u",
        ㅑ: "i",
        ㅐ: "o",
        ㅔ: "p",
        ㅁ: "a",
        ㄴ: "s",
        ㅇ: "d",
        ㄹ: "f",
        ㅎ: "g",
        ㅗ: "h",
        ㅓ: "j",
        ㅏ: "k",
        ㅣ: "l",
        ㅋ: "z",
        ㅌ: "x",
        ㅊ: "c",
        ㅍ: "v",
        ㅠ: "b",
        ㅜ: "n",
        ㅡ: "m",
        // 쌍자음 (Shift)
        ㅃ: "Q",
        ㅉ: "W",
        ㄸ: "E",
        ㄲ: "R",
        ㅆ: "T",
        // 복합 모음
        ㅒ: "O",
        ㅖ: "P",
      };

      // 완성형 한글을 자모로 분리하여 변환
      let result = "";
      for (const char of str) {
        // 이미 매핑된 자모인 경우
        if (korToEng[char]) {
          result += korToEng[char];
          continue;
        }

        const code = char.charCodeAt(0);
        // 완성형 한글 범위 (가 ~ 힣)
        if (code >= 0xac00 && code <= 0xd7a3) {
          const offset = code - 0xac00;
          const cho = Math.floor(offset / 588);
          const jung = Math.floor((offset % 588) / 28);
          const jong = offset % 28;

          const chosung = [
            "ㄱ",
            "ㄲ",
            "ㄴ",
            "ㄷ",
            "ㄸ",
            "ㄹ",
            "ㅁ",
            "ㅂ",
            "ㅃ",
            "ㅅ",
            "ㅆ",
            "ㅇ",
            "ㅈ",
            "ㅉ",
            "ㅊ",
            "ㅋ",
            "ㅌ",
            "ㅍ",
            "ㅎ",
          ];
          const jungsung = [
            "ㅏ",
            "ㅐ",
            "ㅑ",
            "ㅒ",
            "ㅓ",
            "ㅔ",
            "ㅕ",
            "ㅖ",
            "ㅗ",
            "ㅘ",
            "ㅙ",
            "ㅚ",
            "ㅛ",
            "ㅜ",
            "ㅝ",
            "ㅞ",
            "ㅟ",
            "ㅠ",
            "ㅡ",
            "ㅢ",
            "ㅣ",
          ];
          const jongsung = [
            "",
            "ㄱ",
            "ㄲ",
            "ㄳ",
            "ㄴ",
            "ㄵ",
            "ㄶ",
            "ㄷ",
            "ㄹ",
            "ㄺ",
            "ㄻ",
            "ㄼ",
            "ㄽ",
            "ㄾ",
            "ㄿ",
            "ㅀ",
            "ㅁ",
            "ㅂ",
            "ㅄ",
            "ㅅ",
            "ㅆ",
            "ㅇ",
            "ㅈ",
            "ㅊ",
            "ㅋ",
            "ㅌ",
            "ㅍ",
            "ㅎ",
          ];

          // 초성
          if (korToEng[chosung[cho]]) {
            result += korToEng[chosung[cho]];
          }
          // 중성
          if (korToEng[jungsung[jung]]) {
            result += korToEng[jungsung[jung]];
          }
          // 종성
          if (jong > 0 && korToEng[jongsung[jong]]) {
            result += korToEng[jongsung[jong]];
          }
        } else {
          // 한글이 아닌 경우 그대로 유지
          result += char;
        }
      }
      // 바코드 스캔 값은 항상 대문자로 변환
      return result.toUpperCase();
    },

    // GS1-128 바코드 파싱
    parseGS1Barcode(rawValue) {
      const result = {
        gtin: "",
        manufacturingDate: "",
        lotNumber: "",
      };

      // GS1 Application Identifiers
      // (01) = GTIN (14자리)
      // (11) = 제조일자 (YYMMDD, 6자리)
      // (10) = LOT/Batch (가변 길이)

      // 가능한 구분자: 괄호, FNC1 (ASCII 29 = GS), 또는 공백 없이 연속
      let workingValue = rawValue;

      // 괄호 형식 파싱: (01)12345678901234(11)231215(10)ABC123
      const parenPattern = /\((\d{2})\)([^(]+)/g;
      let match;
      let hasParenFormat = false;

      while ((match = parenPattern.exec(rawValue)) !== null) {
        hasParenFormat = true;
        const ai = match[1];
        const value = match[2].trim();

        if (ai === "01") {
          // GTIN: 14자리 숫자
          result.gtin = value.replace(/[^0-9]/g, "").substring(0, 14);
        } else if (ai === "11") {
          // 제조일자: 6자리 (YYMMDD)
          result.manufacturingDate = value
            .replace(/[^0-9]/g, "")
            .substring(0, 6);
        } else if (ai === "10") {
          // LOT: 가변 길이 (최대 20자)
          result.lotNumber = value.substring(0, 20);
        }
      }

      // 괄호 형식이 아닌 경우 연속된 AI 코드 파싱
      if (!hasParenFormat) {
        // GS (Group Separator, ASCII 29) 또는 공백으로 분리된 형식
        // 또는 01XXXXXXXXXXXXXX11YYMMDD10LOTXXX 형식

        // GTIN (01) 파싱 - 14자리 숫자
        const gtinMatch = workingValue.match(/01(\d{14})/);
        if (gtinMatch) {
          result.gtin = gtinMatch[1];
          workingValue = workingValue.replace(/01\d{14}/, "");
        }

        // 제조일자 (11) 파싱 - 6자리 숫자
        const dateMatch = workingValue.match(/11(\d{6})/);
        if (dateMatch) {
          result.manufacturingDate = dateMatch[1];
          workingValue = workingValue.replace(/11\d{6}/, "");
        }

        // LOT (10) 파싱 - 가변 길이 (AI 10 다음부터 끝까지)
        // LOT는 영숫자 및 특수문자 혼합 가능
        const lotMatch = workingValue.match(/10(.+)$/);
        if (lotMatch) {
          result.lotNumber = lotMatch[1].substring(0, 20);
        }
      }

      // Fallback: GS1 AI 형식이 없는 raw EAN-13 / GTIN-14 단독 입력 지원
      // - DB 저장 표준은 13자리 EAN-13이며, 사용자가 GTIN을 직접 복사/수동 입력하는 경우
      //   GS1 AI prefix가 없어 위 정규식들이 GTIN을 추출하지 못함
      // - 이 경우 AI(11) 패턴이 입력값 안에서 우연히 매칭되어 잘못된 manufacturingDate 가 생성될 수 있으므로
      //   GTIN을 raw로 인식할 때는 manufacturingDate / lotNumber 도 함께 폐기
      if (!result.gtin) {
        const digitsOnly = rawValue.replace(/\D/g, "");
        if (digitsOnly.length === 13) {
          result.gtin = digitsOnly;
          result.manufacturingDate = "";
          result.lotNumber = "";
        } else if (digitsOnly.length === 14) {
          // GTIN-14 입력 시 indicator digit 제거하여 EAN-13으로 정규화
          result.gtin = digitsOnly.slice(1);
          result.manufacturingDate = "";
          result.lotNumber = "";
        }
      }

      return result;
    },

    // 바코드 데이터 초기화
    clearBarcodeData() {
      this.barcodeScanValue = "";
      this.gtinCode = "";
      this.manufacturingDate = "";
      this.lotNumber = "";
      this.barcodeParseError = "";
      this.gtinSearchResults = [];
      this.showGtinDropdown = false;
      this.gtinSearchError = ""; // 에러 메시지도 초기화

      // productInfoWithCode 초기화
      if (this.localToothInfo.productInfoWithCode) {
        this.localToothInfo.productInfoWithCode.gtinCode = "";
        this.localToothInfo.productInfoWithCode.manufacturingDate = "";
        this.localToothInfo.productInfoWithCode.lotNumber = "";
      }

      this.emitToothInfo();

      // 클리어 후 바코드 입력란에 포커스 유지
      this.$nextTick(() => {
        this.focusBarcodeInput();
      });
    },

    // 입력 모드 변경 핸들러
    handleInputModeChange() {
      // 모드 변경 시 기존 데이터 초기화
      this.clearBarcodeData();
      this.manualGtinError = "";
      this.manufactureDateError = "";
      // 제품 선택도 초기화
      this.localToothInfo.itemName = "";
      this.emitToothInfo();
    },

    // 직접 입력 모드 - 제조일자 변경 핸들러
    handleManualManufacturingDateChange() {
      // 숫자만 허용
      this.manufacturingDate = this.manufacturingDate.replace(/[^0-9]/g, "");
      // YYMMDD 형식 검증 (6자리 완성 시)
      this.manufactureDateError = "";
      if (this.manufacturingDate.length === 6) {
        const mm = parseInt(this.manufacturingDate.substring(2, 4), 10);
        const dd = parseInt(this.manufacturingDate.substring(4, 6), 10);
        if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
          this.manufactureDateError = this.$t(
            "workorder_err_invalid_date_format"
          );
        }
      }
      this.updateProductInfoWithCode();
    },

    // 직접 입력 모드 - LOT 번호 변경 핸들러
    handleManualLotNumberChange() {
      this.lotNumber = this.lotNumber.toUpperCase();
      this.updateProductInfoWithCode();
    },

    // productInfoWithCode 업데이트 공통 함수
    updateProductInfoWithCode() {
      if (!this.localToothInfo.productInfoWithCode) {
        this.localToothInfo.productInfoWithCode = {};
      }
      this.localToothInfo.productInfoWithCode.gtinCode = this.gtinCode;
      this.localToothInfo.productInfoWithCode.manufacturingDate =
        this.manufacturingDate;
      this.localToothInfo.productInfoWithCode.lotNumber = this.lotNumber;
      this.emitToothInfo();
    },

    // 제조일자 포맷팅 (YYMMDD -> YYYY-MM-DD)
    formatManufacturingDate(dateStr) {
      if (!dateStr || dateStr.length !== 6) return dateStr;

      const yy = dateStr.substring(0, 2);
      const mm = dateStr.substring(2, 4);
      const dd = dateStr.substring(4, 6);

      // 2000년대로 가정 (00-99 -> 2000-2099)
      const year = parseInt(yy) > 50 ? `19${yy}` : `20${yy}`;

      return `${year}-${mm}-${dd}`;
    },
  },
};
</script>
<style lang="scss">
.is-v2-form {
  label.label-column {
    line-height: 1 !important;
    min-height: 40px !important;
    justify-content: center !important;
  }
  input[type="radio"]:checked {
    background-color: #1677ff;
  }
  .ant-radio-wrapper .ant-radio-checked .ant-radio-inner {
    background-color: #1677ff;
    border-color: #1677ff;
  }
  .input-error-wrap {
    line-height: 1 !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }
}

// GTIN 자동완성 드롭다운 옵션 스타일 (unscoped - 드롭다운은 body에 렌더링됨)
.gtin-option-item {
  display: flex;
  flex-direction: column;
  padding: 4px 0;

  .gtin-option-name {
    font-size: 14px;
    font-weight: 500;
    color: #2f3337;
  }

  .gtin-option-code {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
}
</style>
<style scoped lang="scss">
// 치아 번호 선택 섹션
.teeth-number-section {
  width: 100%;
  margin-bottom: 24px;

  .teeth-number-selector {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .selected-teeth-display {
    margin-top: 24px;
    font-size: 15px;
    font-weight: 600;
    text-align: left;
    color: #2f3337;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: "SUIT Variable";
    font-size: 17px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
  }
}

// 라디오 버튼 그룹 스타일
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .radio-label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    span {
      font-size: 15px;
      color: #2f3337;
    }
  }
}

// 2열 레이아웃
.teeth-item-wrap {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.teeth-contents-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.certificate-img-wrapper {
  flex-shrink: 0;
}

// STM 스위치 관련 스타일
.switch-text-wrap {
  display: flex;
  align-items: center;
}

.surface-text-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.certificate-img-area {
  width: 100%;
  height: 350px;
  position: relative;
  overflow: visible;

  .certificate-img.full {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 12px;
    overflow: visible;

    img {
      width: 100% !important;
    }

    .stm-overlay-img {
      position: absolute;
      width: 220px;
      height: 400px;
      top: -10%;
      left: -10%;
      z-index: 3;
      object-fit: contain;
    }

    &.flip {
      transform: rotateY(180deg);
      transition: transform 0.6s;
    }

    .card-back {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transform: rotateY(180deg);
      backface-visibility: hidden;
    }
  }
}

.surface-container {
  margin-top: 20px;

  .surface-title-area {
    .surface-text-button {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .tooltip-label-wrap {
        display: flex;
        align-items: center;
        gap: 8px;

        .question-mark {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #fff;
          border: none;
          color: #acb2b9;
          border: 1px solid #acb2b9;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .label-wrap {
          .label-text {
            font-size: 15px;
            color: #2f3337;
            font-weight: 500;
          }
        }
      }
    }

    .true-color {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.companies-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.change-order-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #dde0e4;
  border-radius: 4px;
  color: #6e747c;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #f1f2f3;
  }
}

.is-certified-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .left-area {
    font-size: 14px;
    font-weight: 500;
    color: #4b5158;
  }
}

@media (max-width: 768px) {
  .teeth-item-wrap {
    flex-direction: column;
  }

  .certificate-img-area {
    height: 250px;
  }
}

// GTIN 코드 입력 관련 스타일
.gtin-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .gtin-error {
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    font-size: 14px;
  }
}

// 바코드 스캔 컨테이너 스타일
.barcode-scan-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .barcode-error {
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    font-size: 13px;
  }
}

// 직접 입력 모드 - GS1 식별자 prefix 스타일
.gs1-prefix {
  color: #6b7280;
  font-weight: 600;
  font-size: 13px;
  padding-right: 8px;
  border-right: 1px solid #d9d9d9;
  margin-right: 8px;
  white-space: nowrap;
  user-select: none;
}

// 직접 입력 모드 - 입력 wrapper
.manual-input-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

// 직접 입력 모드 - 에러 메시지
.manual-input-error {
  margin-top: 6px;
  color: #dc2626;
  font-size: 13px;
}

// 제품 선택 wrapper
.product-select-wrapper {
  width: 100%;
}

// GTIN 검색 에러 메시지
.gtin-error-message {
  margin-top: 8px;
  color: #dc2626;
  font-size: 13px;
  white-space: nowrap;
}

// 파싱된 데이터 표시 섹션
.parsed-data-section {
  margin-top: 8px;
  margin-left: 120px; // row-with-label의 라벨 너비에 맞춤
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;

  @media (max-width: 1000px) {
    margin-left: 0;
  }
}

.parsed-data-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.parsed-data-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .parsed-label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
  }

  .parsed-value {
    font-size: 14px;
    font-weight: 600;
    color: #2f3337;
    font-family: "SF Mono", Monaco, "Consolas", monospace;
  }
}
</style>
<style scoped lang="scss">
@import "./css/issue-certificate.scss";
@import "./css/implant-abutment.scss";
</style>
